import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { cajasAPI, cierresCajaAPI, fondosAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useZonaHoraria } from '../context/ZonaHorariaContext';
import '../Clientes.css';

const formatMoney = (n) =>
  `$${(parseFloat(n) || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TransferenciaChequesCaja = () => {
  const { hasPermission } = useAuth();
  const { fechaHoy, formatFecha } = useZonaHoraria();
  const [cajas, setCajas] = useState([]);
  const [cajaOrigenId, setCajaOrigenId] = useState('');
  const [cajaDestinoId, setCajaDestinoId] = useState('');
  const [cheques, setCheques] = useState([]);
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [fecha, setFecha] = useState('');
  const [concepto, setConcepto] = useState('Transferencia de cheques entre cajas');
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    setFecha((prev) => prev || fechaHoy());
  }, [fechaHoy]);

  useEffect(() => {
    cajasAPI.getAll({ activo: true }).then((r) => {
      if (r.success) setCajas(r.data || []);
    }).catch(() => {});
  }, []);

  const cargarCheques = async () => {
    if (!cajaOrigenId) {
      toast.warning('Seleccione caja origen');
      return;
    }
    try {
      setLoading(true);
      const res = await cierresCajaAPI.chequesEnCaja(cajaOrigenId);
      if (res.success) {
        setCheques(res.data || []);
        setSeleccionados(new Set());
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const totalSeleccionado = useMemo(() => {
    return cheques
      .filter((c) => seleccionados.has(c.id))
      .reduce((s, c) => s + (parseFloat(c.importe) || 0), 0);
  }, [cheques, seleccionados]);

  const toggle = (id) => {
    setSeleccionados((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const transferir = async () => {
    if (!cajaOrigenId || !cajaDestinoId || cajaOrigenId === cajaDestinoId) {
      toast.warning('Seleccione dos cajas distintas');
      return;
    }
    if (seleccionados.size === 0) {
      toast.warning('Seleccione al menos un cheque');
      return;
    }
    try {
      setGuardando(true);
      const res = await fondosAPI.crear({
        fecha,
        concepto,
        clase_movimiento: 'transferencia_cheques_caja',
        caja_origen_id: Number(cajaOrigenId),
        caja_destino_id: Number(cajaDestinoId),
        cheques_ids: [...seleccionados],
      });
      if (!res.success) throw new Error(res.message);
      toast.success(res.message || 'Transferencia registrada');
      setSeleccionados(new Set());
      cargarCheques();
    } catch (e) {
      toast.error(e.message || 'Error al transferir');
    } finally {
      setGuardando(false);
    }
  };

  if (!hasPermission('cerrar_caja') && !hasPermission('crear_movimientos_fondos')) {
    return <div className="clientes"><p>Sin permiso.</p></div>;
  }

  return (
    <div className="clientes pagina-listado pagina-listado--scroll">
      <div className="page-header">
        <h1>Transferencia de cheques entre cajas</h1>
        <Link to="/cierre-caja" className="btn-secondary">Cierre de caja</Link>
      </div>

      <div className="listado-panel clientes-listado listado-controles">
        <div className="filters-section" style={{ marginBottom: 0 }}>
        <div className="filters-grid">
          <div className="filter-group">
            <label>Caja origen *</label>
            <select value={cajaOrigenId} onChange={(e) => setCajaOrigenId(e.target.value)}>
              <option value="">Seleccione...</option>
              {cajas.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label>Caja destino *</label>
            <select value={cajaDestinoId} onChange={(e) => setCajaDestinoId(e.target.value)}>
              <option value="">Seleccione...</option>
              {cajas.filter((c) => String(c.id) !== String(cajaOrigenId)).map((c) => (
                <option key={c.id} value={c.id}>{c.nombre}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Fecha</label>
            <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </div>
          <div className="filter-group" style={{ alignSelf: 'end' }}>
            <button type="button" className="btn-secondary" onClick={cargarCheques} disabled={loading}>
              {loading ? 'Cargando…' : 'Cargar cheques'}
            </button>
          </div>
        </div>
        <div className="filter-group" style={{ marginTop: 12 }}>
          <label>Concepto</label>
          <input type="text" value={concepto} onChange={(e) => setConcepto(e.target.value)} style={{ maxWidth: 480, width: '100%' }} />
        </div>
        </div>

        <div className="listado-panel-cuerpo clientes-listado-cuerpo">
          <div className="listado-tabla-area clientes-listado-tabla-area">
            <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th></th>
              <th>N° cheque</th>
              <th>Banco</th>
              <th>Cliente</th>
              <th>Importe</th>
              <th>F. cobro</th>
            </tr>
          </thead>
          <tbody>
            {cheques.length === 0 ? (
              <tr><td colSpan={6} className="no-data">Cargue cheques de la caja origen</td></tr>
            ) : (
              cheques.map((ch) => (
                <tr key={ch.id}>
                  <td>
                    <input type="checkbox" checked={seleccionados.has(ch.id)} onChange={() => toggle(ch.id)} />
                  </td>
                  <td>{ch.numero}</td>
                  <td>{ch.banco_nombre || '—'}</td>
                  <td>{ch.cliente_razon_social}</td>
                  <td>{formatMoney(ch.importe)}</td>
                  <td>{ch.fecha_cobro ? formatFecha(ch.fecha_cobro) : '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {cheques.length > 0 && (
          <div style={{ padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <strong>Total seleccionado: {formatMoney(totalSeleccionado)} ({seleccionados.size} cheque(s))</strong>
            <button type="button" className="btn-primary" onClick={transferir} disabled={guardando}>
              {guardando ? 'Transfiriendo…' : 'Confirmar transferencia'}
            </button>
          </div>
        )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransferenciaChequesCaja;
